import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';

const DEFAULT_BUSINESS_QUEUE = 'all';
const DEFAULT_MARKET_STATUS = 'all';

const EMPTY_FILTERS = {
  businessQueue: DEFAULT_BUSINESS_QUEUE,
  marketStatus: DEFAULT_MARKET_STATUS,
  sortBy: 'updated_desc',
  status: '',
  lifecycleStatus: '',
  accountStatus: '',
  hiddenFromConsumer: '',
  consumerVisibleReady: '',
  hasActiveStore: '',
  walletState: '',
  fundingMode: '',
  market: '',
  clearinghouse: '',
  updatedFrom: '',
  updatedTo: '',
};

const BUSINESS_FILTER_PRESETS = [
  { key: 'all', label: 'All' },
  { key: 'live_active', label: 'Live In App' },
  { key: 'active_visible', label: 'Active & Visible' },
  { key: 'funding_not_set', label: 'Funding Not Set' },
  { key: 'insufficient_funds', label: 'Insufficient Funds Block' },
  { key: 'pending_funding_risk', label: 'Pending Funding Risk' },
  { key: 'hidden', label: 'Hidden from Consumer' },
  { key: 'active_no_active_store', label: 'Active Brand / No Active Store' },
  { key: 'lifecycle_not_active', label: 'Operational Stage Not Active' },
  { key: 'low_depleted_wallet', label: 'Low Balance / Depleted' },
  { key: 'tip_decision_undecided', label: 'Tip Policy Needs Review' },
  { key: 'geography_review', label: 'Geography Review' },
  { key: 'new_pending_review', label: 'Onboarding / Pending Verification' },
];

const LEGACY_QUEUE_TO_BUSINESS_QUEUE = {
  live: 'live_active',
  live_in_app: 'live_active',
  active_visible: 'active_visible',
  funding_not_set: 'funding_not_set',
  insufficient_funds: 'insufficient_funds',
  pending_funding_risk: 'pending_funding_risk',
  hidden: 'hidden',
  active_no_active_store: 'active_no_active_store',
  lifecycle_not_active: 'lifecycle_not_active',
  low_depleted_wallet: 'low_depleted_wallet',
  tip_decision_undecided: 'tip_decision_undecided',
  geography_review: 'geography_review',
  onboarding_pending_verification: 'new_pending_review',
  action_required: 'needs_attention',
};

const CLEARINGHOUSE_FILTERS = [
  { key: 'visible_period_unfunded_liability', label: 'Hard Liabilities', state: 'visible_period_unfunded_liability' },
  { key: 'hidden_period_deferred_candidate', label: 'Hidden Candidates', state: 'hidden_period_deferred_candidate' },
  { key: 'expired_deferred_candidate', label: 'Expired Deferred', state: 'expired_deferred_candidate' },
  { key: 'rejected_on_reprocess', label: 'Rejected Reprocess', state: 'rejected_on_reprocess' },
  { key: 'refund_offset', label: 'Refund Offsets', state: 'refund_offset' },
  { key: 'reactivation_blocked', label: 'Reactivation Blocks', state: '' },
];

function toSearchParams(base = {}) {
  const params = new URLSearchParams();
  Object.entries(base).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') params.set(key, String(value));
  });
  return params;
}

function formatMoney(cents) {
  const n = Number(cents || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n / 100);
}

function titleCaseWords(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatMarketLifecycleLabel(market = {}) {
  const status = titleCaseWords(market?.marketStatus || '');
  if (!status) return market?.displayName || 'Market';
  if (market?.activationPath === 'active_adjacent' && market?.marketStatus === 'developing') {
    return `${market.displayName} • ${status} (Adjacent)`;
  }
  return `${market.displayName} • ${status}`;
}

function formatCauseLabel(code, note) {
  if (!code) return '—';
  const label = titleCaseWords(code);
  return note ? `${label}: ${note}` : label;
}

const BLOCKER_LABELS = {
  account_not_active: 'Account Hold',
  wallet_not_funded: 'Wallet Not Funded',
  wallet_depleted: 'Wallet Depleted',
  wallet_below_minimum: 'Wallet Below Minimum',
  funding_safety_override: 'Funding Safety Block',
  auto_topup_failed: 'Auto Top-up Failed',
  payment_method_expiring: 'Payment Method Expiring',
  brand_not_active: 'EIN Inactive',
  brand_lifecycle_not_active: 'Lifecycle Hold',
  manual_override: 'Manual Override',
  no_included_active_store: 'No Included Active Store',
  store_temp_excluded: 'Store Temp Excluded',
  geography_unresolved: 'Geography Review',
  attribution_mapping_missing: 'Mapping Missing',
  insufficient_wallet_funds: 'Insufficient Wallet Funds',
  topup_reversed: 'Top-up Reversed',
  topup_disputed: 'Top-up Disputed',
  wallet_insolvent: 'Wallet Insolvent',
};

const LOGGED_UNKNOWN_BLOCKERS = new Set();

function formatBlockerCode(code) {
  if (BLOCKER_LABELS[code]) return BLOCKER_LABELS[code];
  if (process.env.NODE_ENV !== 'production' && code && !LOGGED_UNKNOWN_BLOCKERS.has(code)) {
    LOGGED_UNKNOWN_BLOCKERS.add(code);
    // Surface new blocker codes during development so the curated label map stays in sync.
    console.warn('Unknown blocker code in OpsBusinessMonitoring:', code);
  }
  return 'Other issue';
}

function deriveQuickBlockers(row = {}) {
  const accountBlockers = Array.isArray(row?.blockers?.account) ? row.blockers.account : [];
  const einBlockers = Array.isArray(row?.blockers?.ein) ? row.blockers.ein : [];
  const storeBlockers = Array.isArray(row?.blockers?.store) ? row.blockers.store : [];
  const fundingSafetyReasonCode = String(
    row?.fundingSafety?.reasonCode ||
    row?.queueFlags?.fundingSafetyReasonCode ||
    ''
  ).trim();
  const fundingSafetyActive = !!row?.fundingSafety?.active || !!row?.queueFlags?.insufficientFundsBlocked || !!fundingSafetyReasonCode;
  const aggregated = new Map();

  if (fundingSafetyActive) {
    aggregated.set('account:funding_safety_override', {
      scope: 'account',
      severity: 'High',
      code: 'funding_safety_override',
      causeCode: fundingSafetyReasonCode || 'wallet_insolvent',
      causeNote: row?.fundingSafety?.reasonNote || null,
      count: 1,
      primaryStoreId: '',
    });
  }

  if (row?.geography?.hasUnresolved) {
    aggregated.set('store:geography_unresolved', {
      scope: 'store',
      severity: 'High',
      code: 'geography_unresolved',
      causeCode: row?.geography?.reasonCode || 'needs_ops_review',
      causeNote: Number(row?.geography?.unresolvedStoreCount || 0) > 0
        ? `${Number(row.geography.unresolvedStoreCount)} store location(s) need geography resolution`
        : 'Business geography needs Ops review',
      count: Math.max(1, Number(row?.geography?.unresolvedStoreCount || 0)),
      primaryStoreId: '',
    });
  }

  [...accountBlockers, ...einBlockers, ...storeBlockers].forEach((blocker) => {
    if (!blocker?.code) return;
    const aggregateStore = blocker?.scope === 'store';
    const key = aggregateStore ? `store:${blocker.code}` : `${blocker?.scope || ''}:${blocker.code}`;
    if (!aggregated.has(key)) {
      aggregated.set(key, {
        ...blocker,
        count: 1,
        primaryStoreId: blocker?.storeId || '',
      });
      return;
    }
    const current = aggregated.get(key);
    current.count += 1;
    if (!current.primaryStoreId && blocker?.storeId) current.primaryStoreId = blocker.storeId;
  });

  return Array.from(aggregated.values());
}

function blockerActionForCode(code) {
  if (code === 'account_not_active') return 'accountStatus';
  if (code === 'brand_not_active') return 'brandStatus';
  if (code === 'brand_lifecycle_not_active') return 'lifecycleStatus';
  if (code === 'manual_override') return 'hiddenFromConsumer';
  if (code === 'no_included_active_store' || code === 'store_temp_excluded') return 'storeEnrollment';
  if (code === 'geography_unresolved') return 'geography';
  return '';
}

function getDirectBlockerAction(blocker = {}) {
  const direct = (Array.isArray(blocker?.actions) ? blocker.actions : []).find((action) => action?.opsCanExecute && action?.key);
  if (direct?.key) return direct.key;
  return blockerActionForCode(blocker?.code || '');
}

function formatBlockerScope(scope) {
  if (scope === 'account') return 'Account blocker';
  if (scope === 'ein') return 'EIN blocker';
  if (scope === 'store') return 'Store blocker';
  return 'Readiness blocker';
}

function formatBlockerBadgeLabel(blocker = {}) {
  const base = formatBlockerCode(blocker?.code);
  if (Number(blocker?.count || 0) > 1) return `${base} (${Number(blocker.count)})`;
  if (blocker?.code === 'no_included_active_store' && Number(blocker?.metadata?.storeCount || 0) > 1) {
    return `${base} (${Number(blocker.metadata.storeCount)})`;
  }
  return base;
}

function buildDetailHref(row = {}, blocker = null) {
  const params = new URLSearchParams();
  const action = blocker ? getDirectBlockerAction(blocker) : '';
  if (action) params.set('action', action);
  if (row?.id) params.set('einId', String(row.id));
  const targetStoreId = blocker?.primaryStoreId || blocker?.storeId || '';
  if (targetStoreId) params.set('storeId', String(targetStoreId));
  return `/ops/businesses/${row.id || row.brandId}${params.toString() ? `?${params.toString()}` : ''}`;
}

function formatStoreBlockerEntry(blocker = {}) {
  const storeName = blocker?.metadata?.storeName || blocker?.storeId || 'Store';
  const suffix = blocker?.causeCode ? ` - ${formatCauseLabel(blocker.causeCode, blocker.causeNote)}` : '';
  return `${storeName}: ${formatBlockerCode(blocker.code)}${suffix}`;
}

export default function OpsBusinessMonitoring() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ markets: [], businessQueues: [], marketStatuses: [] });
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedStoreBreakdowns, setExpandedStoreBreakdowns] = useState({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [clearinghouseSummary, setClearinghouseSummary] = useState(null);
  const [clearinghouseStates, setClearinghouseStates] = useState([]);
  const [clearinghouseLoading, setClearinghouseLoading] = useState(false);
  const limit = 50;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    const legacyQueue = next.get('queue') || next.get('view') || next.get('savedView') || '';
    const mappedQueue = LEGACY_QUEUE_TO_BUSINESS_QUEUE[legacyQueue] || '';
    if (mappedQueue && !next.get('businessQueue')) {
      next.set('businessQueue', mappedQueue);
      changed = true;
    }
    ['queue', 'view', 'savedView'].forEach((key) => {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    });
    if (!next.get('businessQueue')) {
      next.set('businessQueue', 'all');
      changed = true;
    } else if (next.get('businessQueue') === 'action_required') {
      next.set('businessQueue', 'needs_attention');
      changed = true;
    }
    if (!next.get('marketStatus')) {
      next.set('marketStatus', 'all');
      changed = true;
    } else if (next.get('marketStatus') === 'funding_relevant') {
      next.set('marketStatus', 'all');
      changed = true;
    }
    if (next.get('market') === 'all') {
      next.set('market', '');
      changed = true;
    }
    if (!next.get('sortBy')) {
      next.set('sortBy', EMPTY_FILTERS.sortBy);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const incoming = { ...EMPTY_FILTERS };
    Object.keys(EMPTY_FILTERS).forEach((key) => {
      const v = searchParams.get(key);
      if (v != null) incoming[key] = v;
    });
    setFilters(incoming);
    const hasActiveFilter = Object.entries(incoming).some(([k, value]) => String(value || '') !== String(EMPTY_FILTERS[k] || ''));
    setFiltersOpen(hasActiveFilter);
    const pageRaw = Number(searchParams.get('page'));
    setPage(Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams(searchParams);
        const legacyQueue = params.get('queue') || params.get('view') || params.get('savedView') || '';
        const mappedQueue = LEGACY_QUEUE_TO_BUSINESS_QUEUE[legacyQueue] || '';
        if (mappedQueue && !params.get('businessQueue')) params.set('businessQueue', mappedQueue);
        ['queue', 'view', 'savedView'].forEach((key) => params.delete(key));
        if (params.get('businessQueue') === 'action_required') params.set('businessQueue', 'needs_attention');
        if (params.get('marketStatus') === 'funding_relevant') params.set('marketStatus', 'all');
        if (params.get('market') === 'all') params.set('market', '');
        params.delete('clearinghouse');
        if (!params.get('businessQueue')) params.set('businessQueue', DEFAULT_BUSINESS_QUEUE);
        if (!params.get('marketStatus')) params.set('marketStatus', DEFAULT_MARKET_STATUS);
        if (!params.get('sortBy')) params.set('sortBy', EMPTY_FILTERS.sortBy);
        params.set('limit', String(limit));
        params.set('page', String(page));
        const data = await apiFetch(`/ops/businesses?${params.toString()}`);
        if (cancelled) return;
        setRows(Array.isArray(data?.businesses) ? data.businesses : []);
        setTotal(Number(data?.total || 0));
        setFilterOptions({
          markets: Array.isArray(data?.filters?.markets) ? data.filters.markets : [],
          businessQueues: Array.isArray(data?.filters?.businessQueues) ? data.filters.businessQueues : [],
          marketStatuses: Array.isArray(data?.filters?.marketStatuses) ? data.filters.marketStatuses : [],
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401 || err?.status === 403) {
          clearAuthToken();
          navigate('/login', { replace: true });
          return;
        }
        setError(err?.data?.error || err?.message || 'Failed to load business monitoring list');
        setRows([]);
        setTotal(0);
        setFilterOptions({ markets: [], businessQueues: [], marketStatuses: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [navigate, page, searchParams]);

  useEffect(() => {
    let cancelled = false;
    async function loadClearinghouse() {
      setClearinghouseLoading(true);
      try {
        const selected = CLEARINGHOUSE_FILTERS.find((item) => item.key === filters.clearinghouse);
        const stateParams = new URLSearchParams();
        stateParams.set('limit', '25');
        if (selected?.state) stateParams.set('state', selected.state);
        else stateParams.set('needsAttention', 'true');
        const [summaryData, statesData] = await Promise.all([
          apiFetch('/ops/clearinghouse/summary'),
          apiFetch(`/ops/clearinghouse/states?${stateParams.toString()}`),
        ]);
        if (cancelled) return;
        setClearinghouseSummary(summaryData?.summary || summaryData || null);
        setClearinghouseStates(Array.isArray(statesData?.states) ? statesData.states : []);
      } catch (_err) {
        if (cancelled) return;
        setClearinghouseSummary(null);
        setClearinghouseStates([]);
      } finally {
        if (!cancelled) setClearinghouseLoading(false);
      }
    }
    loadClearinghouse();
    return () => { cancelled = true; };
  }, [filters.clearinghouse]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const accountGroups = useMemo(() => {
    const groups = [];
    const byAccount = new Map();

    rows.forEach((row) => {
      const key = String(row.accountId || 'unassigned');
      let group = byAccount.get(key);
      if (!group) {
        group = {
          key,
          accountId: row.accountId || null,
          rows: [],
          einCount: 0,
          totalStores: 0,
          activeStores: 0,
          blockers: [],
        };
        byAccount.set(key, group);
        groups.push(group);
      }

      group.rows.push(row);
      group.einCount += 1;
      group.totalStores += Number(row.stores?.total || 0);
      group.activeStores += Number(row.stores?.active || 0);
      const accountOnly = Array.isArray(row?.blockers?.account) ? row.blockers.account : [];
      accountOnly.forEach((blocker) => {
        const keyForBlocker = `${blocker?.scope || ''}:${blocker?.code || ''}`;
        const exists = group.blockers.some((entry) => `${entry?.scope || ''}:${entry?.code || ''}` === keyForBlocker);
        if (!exists) group.blockers.push(blocker);
      });
    });

    return groups;
  }, [rows]);

  useEffect(() => {
    setExpandedAccounts((prev) => {
      const next = {};

      accountGroups.forEach((group) => {
        next[group.key] = Object.prototype.hasOwnProperty.call(prev, group.key)
          ? prev[group.key]
          : true;
      });

      return next;
    });
  }, [accountGroups]);

  function applyFilters() {
    const params = { page: 1 };
    Object.entries(filters).forEach(([k, v]) => {
      if (String(v || '') !== '') params[k] = v;
    });
    setSearchParams(toSearchParams(params));
  }

  function clearFilters() {
    const cleared = { ...EMPTY_FILTERS, sortBy: filters.sortBy || 'updated_desc' };
    setFilters(cleared);
    const params = { page: 1 };
    if (cleared.sortBy) params.sortBy = cleared.sortBy;
    params.businessQueue = cleared.businessQueue;
    params.marketStatus = cleared.marketStatus;
    setSearchParams(toSearchParams(params));
  }

  function applyPreset(key) {
    const nextQueue = key || 'all';
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    params.set('businessQueue', nextQueue);
    params.delete('queue');
    params.delete('view');
    params.delete('savedView');
    setFilters((prev) => ({ ...prev, businessQueue: nextQueue }));
    setSearchParams(params);
  }

  const activePreset = filters.businessQueue || DEFAULT_BUSINESS_QUEUE;
  const activeClearinghouseFilter = filters.clearinghouse || '';
  const clearinghouseCards = [
    {
      key: 'visible_period_unfunded_liability',
      label: 'Hard Liabilities',
      count: clearinghouseSummary?.visiblePeriodUnfundedLiabilities?.count || 0,
      caption: `${formatMoney(clearinghouseSummary?.visiblePeriodUnfundedLiabilities?.amountCents)} unsettled`,
    },
    {
      key: 'hidden_period_deferred_candidate',
      label: 'Hidden Candidates',
      count: clearinghouseSummary?.hiddenPeriodDeferredCandidates?.count || 0,
      caption: `${formatMoney(clearinghouseSummary?.hiddenPeriodDeferredCandidates?.potentialAmountCents)} potential`,
    },
    {
      key: 'reactivation_blocked',
      label: 'Reactivation Blocks',
      count: clearinghouseSummary?.brandsBlockedFromReactivation?.count || 0,
      caption: 'Recent blocked restores',
    },
    {
      key: 'refund_offset',
      label: 'Refund Offsets',
      count: clearinghouseSummary?.refundOffsets?.count || 0,
      caption: `${formatMoney(clearinghouseSummary?.refundOffsets?.amountCents)} offset`,
    },
  ];

  function applyClearinghouseFilter(key) {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (key) params.set('clearinghouse', key);
    else params.delete('clearinghouse');
    setFilters((prev) => ({ ...prev, clearinghouse: key || '' }));
    setSearchParams(params);
  }

  return (
    <OpsLayout title="Business Monitoring" subtitle="Triage and manage business participation/visibility readiness.">
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Clearinghouse Attention</h3>
            <button type="button" className="ops-btn" onClick={() => applyClearinghouseFilter('')}>Clear</button>
          </div>
          <div className="ops-business-presets" aria-label="Clearinghouse filters">
            {clearinghouseCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className={`ops-business-preset ${activeClearinghouseFilter === card.key ? 'active' : ''}`}
                onClick={() => applyClearinghouseFilter(card.key)}
              >
                {card.label}: {Number(card.count || 0).toLocaleString('en-US')} • {card.caption}
              </button>
            ))}
          </div>
        </section>

        <section className="ops-business-presets" aria-label="Business monitoring saved filters">
          {BUSINESS_FILTER_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={`ops-business-preset ${activePreset === preset.key ? 'active' : ''}`}
              onClick={() => applyPreset(preset.key)}
            >
              {preset.label}
            </button>
          ))}
        </section>

        <section className="ops-nfp-panel">
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Filters</h3>
            <button type="button" className="ops-btn" onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen}>
              {filtersOpen ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
          {filtersOpen && (
            <div className="ops-nfp-panel-body">
              <div className="ops-nfp-grid">
                <div className="ops-nfp-field">
                  <label>Business Queue</label>
                  <select
                    value={filters.businessQueue}
                    onChange={(e) => setFilters((p) => ({ ...p, businessQueue: e.target.value, market: '' }))}
                  >
                    {(Array.isArray(filterOptions.businessQueues) ? filterOptions.businessQueues : []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Market Status</label>
                  <select
                    value={filters.marketStatus}
                    onChange={(e) => setFilters((p) => ({ ...p, marketStatus: e.target.value, market: '' }))}
                  >
                    {(Array.isArray(filterOptions.marketStatuses) ? filterOptions.marketStatuses : []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Brand Status</label>
                  <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                    <option value="">All</option>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Operational Stage</label>
                  <select value={filters.lifecycleStatus} onChange={(e) => setFilters((p) => ({ ...p, lifecycleStatus: e.target.value }))}>
                    <option value="">All</option>
                    <option value="prospect">prospect</option>
                    <option value="onboarding">onboarding</option>
                    <option value="pending_verification">pending_verification</option>
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                    <option value="closed">closed</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Account Status</label>
                  <select value={filters.accountStatus} onChange={(e) => setFilters((p) => ({ ...p, accountStatus: e.target.value }))}>
                    <option value="">All</option>
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Hidden From Consumer</label>
                  <select value={filters.hiddenFromConsumer} onChange={(e) => setFilters((p) => ({ ...p, hiddenFromConsumer: e.target.value }))}>
                    <option value="">All</option>
                    <option value="true">yes</option>
                    <option value="false">no</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Discovery Ready</label>
                  <select value={filters.consumerVisibleReady} onChange={(e) => setFilters((p) => ({ ...p, consumerVisibleReady: e.target.value }))}>
                    <option value="">All</option>
                    <option value="true">yes</option>
                    <option value="false">no</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Has Active Store</label>
                  <select value={filters.hasActiveStore} onChange={(e) => setFilters((p) => ({ ...p, hasActiveStore: e.target.value }))}>
                    <option value="">All</option>
                    <option value="true">yes</option>
                    <option value="false">no</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Wallet State</label>
                  <select value={filters.walletState} onChange={(e) => setFilters((p) => ({ ...p, walletState: e.target.value }))}>
                    <option value="">All</option>
                    <option value="funded">funded</option>
                    <option value="low">low</option>
                    <option value="depleted">depleted</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Funding Mode</label>
                  <select value={filters.fundingMode} onChange={(e) => setFilters((p) => ({ ...p, fundingMode: e.target.value }))}>
                    <option value="">All</option>
                    <option value="auto">auto</option>
                    <option value="manual">manual</option>
                    <option value="not_set">not_set</option>
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Market</label>
                  <select value={filters.market} onChange={(e) => setFilters((p) => ({ ...p, market: e.target.value }))}>
                    <option value="">All</option>
                    {(Array.isArray(filterOptions.markets) ? filterOptions.markets : []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Clearinghouse State</label>
                  <select value={filters.clearinghouse} onChange={(e) => setFilters((p) => ({ ...p, clearinghouse: e.target.value }))}>
                    <option value="">All</option>
                    {CLEARINGHOUSE_FILTERS.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Updated From</label>
                  <input type="date" value={filters.updatedFrom} onChange={(e) => setFilters((p) => ({ ...p, updatedFrom: e.target.value }))} />
                </div>
                <div className="ops-nfp-field">
                  <label>Updated To</label>
                  <input type="date" value={filters.updatedTo} onChange={(e) => setFilters((p) => ({ ...p, updatedTo: e.target.value }))} />
                </div>
              </div>
              <div className="ops-nfp-filter-actions">
                <button type="button" className="ops-btn primary" onClick={applyFilters}>Apply Filters</button>
                <button type="button" className="ops-btn" onClick={clearFilters}>Clear</button>
              </div>
            </div>
          )}
        </section>

        {activeClearinghouseFilter ? (
          <section className="ops-nfp-panel">
            <div className="ops-nfp-panel-header">
              <h3 className="ops-section-title">Clearinghouse State Records</h3>
              <span className="ops-chip">{CLEARINGHOUSE_FILTERS.find((item) => item.key === activeClearinghouseFilter)?.label || activeClearinghouseFilter}</span>
            </div>
            {clearinghouseLoading ? <div className="ops-message">Loading clearinghouse records…</div> : null}
            {!clearinghouseLoading ? (
              <div className="ops-nfp-table-wrap">
                <table className="ops-nfp-table">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>State</th>
                      <th>Txn Date</th>
                      <th>Contribution</th>
                      <th>Fee</th>
                      <th>Refund Offset</th>
                      <th>Decision</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clearinghouseStates.length === 0 ? (
                      <tr><td colSpan={8}>No clearinghouse records matched this filter.</td></tr>
                    ) : clearinghouseStates.map((row) => (
                      <tr key={row.transactionId}>
                        <td>{row.brandName || row.matchedBrandId || '—'}</td>
                        <td><span className={`ops-chip ${row.severity === 'red' ? 'bad' : row.severity === 'yellow' ? 'info' : ''}`}>{titleCaseWords(row.clearinghouseState)}</span></td>
                        <td>{row.transactionDate ? new Date(row.transactionDate).toLocaleDateString() : '—'}</td>
                        <td>{formatMoney(row.calculatedContributionCents || row.contributionCents)}</td>
                        <td>{formatMoney(row.calculatedFeeCents || row.feeCents)}</td>
                        <td>{formatMoney(row.clearinghouseRefundOffsetTotalCents)}</td>
                        <td>{row.suggestedAction ? titleCaseWords(row.suggestedAction) : '—'}</td>
                        <td>
                          {row.matchedBrandId ? (
                            <button type="button" className="ops-btn" onClick={() => navigate(`/ops/businesses/${row.matchedBrandId}`)}>
                              Open detail
                            </button>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="ops-nfp-panel">
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Businesses</h3>
            <div className="ops-nfp-list-controls">
              <label htmlFor="biz-sort-select">Sort</label>
              <select
                id="biz-sort-select"
                value={filters.sortBy}
                onChange={(e) => {
                  const nextSort = e.target.value;
                  setFilters((p) => ({ ...p, sortBy: nextSort }));
                  const p = new URLSearchParams(searchParams);
                  p.set('sortBy', nextSort);
                  p.set('page', '1');
                  setSearchParams(p);
                }}
              >
                <option value="updated_desc">updated_desc (default)</option>
                <option value="updated_asc">updated_asc</option>
                <option value="queue_age_desc">queue_age_desc</option>
                <option value="queue_age_asc">queue_age_asc</option>
                <option value="pending_age_asc">pending_age_asc</option>
              </select>
            </div>
          </div>
          {loading && <div>Loading business queue…</div>}
          {!loading && error && <div>{error}</div>}
          {!loading && !error && (
            <div className="ops-nfp-table-wrap">
              <table className="ops-nfp-table ops-business-monitoring-table">
                <thead>
                  <tr>
                    <th>Brand Name</th>
                    <th>Markets</th>
                    <th title="Store/root geography that needs Ops resolution before market-dependent behavior can proceed.">Geography</th>
                    <th title="Change the selected EIN status.">Brand Status</th>
                    <th title="Change the selected EIN operational stage.">Operational Stage</th>
                    <th title="Review the account status for this wallet footprint.">Account Status</th>
                    <th title="Review consumer discovery for this account.">Consumer Discovery</th>
                    <th>Visible in App</th>
                    <th title="Review the current funding mode.">Funding Mode</th>
                    <th title="Change tip classification in business detail.">Tip Decision</th>
                    <th>Tip Review</th>
                    <th>Account Wallet Balance</th>
                    <th>Pending Exposure</th>
                    <th>Projected Available</th>
                    <th>Account Wallet State</th>
                    <th title="Change store coverage in business detail.">Active/Total Stores</th>
                    <th>Last Top-up</th>
                    <th>Updated At</th>
                    <th>Blockers</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={20}>No businesses matched this view/filter.</td>
                    </tr>
                  )}
                  {accountGroups.map((group) => {
                    const expanded = expandedAccounts[group.key] !== false;

                    return (
                      <React.Fragment key={group.key}>
                        <tr>
                          <td colSpan={20} style={{ padding: 0 }}>
                            <button
                              type="button"
                              className="ops-btn"
                              onClick={() => setExpandedAccounts((prev) => ({ ...prev, [group.key]: !expanded }))}
                              aria-expanded={expanded}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                padding: '12px 14px',
                                border: 'none',
                                borderRadius: 0,
                                background: '#f4f7fb',
                                fontWeight: 700,
                              }}
                            >
                              <span>{expanded ? '▾' : '▸'} {group.accountId ? `Account ${group.accountId}` : 'Unassigned Account'}</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <span>{group.einCount} EIN{group.einCount === 1 ? '' : 's'} • {group.activeStores}/{group.totalStores} stores active</span>
                                {group.blockers.slice(0, 3).map((blocker) => (
                                  <span
                                    key={`group:${group.key}:${blocker.scope}:${blocker.code}`}
                                    className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                                    style={{ fontSize: 12 }}
                                    title={`${formatBlockerScope(blocker.scope)}: ${formatBlockerCode(blocker.code)}${blocker.causeCode ? ` - ${formatCauseLabel(blocker.causeCode, blocker.causeNote)}` : ''}`}
                                  >
                                    {formatBlockerCode(blocker.code)}
                                  </span>
                                ))}
                              </span>
                            </button>
                          </td>
                        </tr>
                          {expanded && group.rows.map((row) => (
                          <tr key={`${group.key}:${row.id || row.brandId}`}>
                            <td>{row.name || row.brandId || 'Unknown'}</td>
                            <td>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {(Array.isArray(row?.markets) ? row.markets : []).map((market) => (
                                  <span key={`${row.id || row.brandId}:${market.marketKey}`} className="ops-chip" title={`${market.marketStatus}${market.activationPath === 'active_adjacent' ? ' • active_adjacent' : ''}`}>
                                    {formatMarketLifecycleLabel(market)}
                                  </span>
                                ))}
                                {(!Array.isArray(row?.markets) || row.markets.length === 0) ? '—' : null}
                              </div>
                            </td>
                            <td>
                              {row?.geography?.hasUnresolved ? (
                                <button
                                  type="button"
                                  className="ops-chip bad"
                                  style={{ border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                                  title={`${Number(row?.geography?.unresolvedStoreCount || 0)} store location(s) need geography resolution. Open detail to retry, resolve, or mark out-of-footprint.`}
                                  onClick={() => navigate(buildDetailHref(row, { code: 'geography_unresolved' }))}
                                >
                                  Geography Review
                                  {Number(row?.geography?.unresolvedStoreCount || 0) > 0 ? ` (${Number(row.geography.unresolvedStoreCount)})` : ''}
                                </button>
                              ) : (
                                <span className="ops-chip good">Resolved</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`ops-chip ${row.status === 'active' ? 'good' : 'bad'}`}
                                title={`Current cause: ${formatCauseLabel(row.causes?.brandStatusCauseCode, row.causes?.brandStatusCauseNote)}`}
                              >
                                {row.status || '—'}
                              </span>
                            </td>
                            <td>
                              <span
                                className="ops-chip"
                                title={`Current cause: ${formatCauseLabel(row.causes?.lifecycleStatusCauseCode, row.causes?.lifecycleStatusCauseNote)}`}
                              >
                                {row.lifecycleStatus || '—'}
                              </span>
                            </td>
                            <td>
                              <div>
                                <span
                                  className={`ops-chip ${row.accountStatus === 'active' ? 'good' : 'bad'}`}
                                  title={`Current cause: ${formatCauseLabel(row.causes?.accountStatusCauseCode, row.causes?.accountStatusCauseNote)}`}
                                >
                                  {row.accountStatus || '—'}
                                </span>
                              </div>
                              {row.causes?.accountStatusCauseCode ? (
                                <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>
                                  {formatCauseLabel(row.causes.accountStatusCauseCode, row.causes.accountStatusCauseNote)}
                                </div>
                              ) : null}
                              {row?.queueFlags?.insufficientFundsBlocked || row?.fundingSafety?.active ? (
                                <div style={{ marginTop: 4, color: '#b45309', fontSize: 12, fontWeight: 600 }}>
                                  Funding safety: {formatCauseLabel(row?.fundingSafety?.reasonCode || row?.queueFlags?.fundingSafetyReasonCode, row?.fundingSafety?.reasonNote)}
                                </div>
                              ) : null}
                            </td>
                            <td>{row.visibility?.consumerVisibleReady ? 'Visible' : 'Hidden'}</td>
                            <td>{row.visibility?.consumerVisibleReady ? 'yes' : 'no'}</td>
                            <td>{row.fundingMode || '—'}</td>
                            <td>{row.tipDecision?.decision || '—'}</td>
                            <td>{row.tipDecision?.unableToDecide ? 'yes' : 'no'}</td>
                            <td>{formatMoney(row.accountWallet?.balanceCents ?? row.wallet?.balanceCents)}</td>
                            <td>{formatMoney(row.accountWallet?.fundingExposure?.pendingExposureCents ?? row.fundingExposure?.pendingExposureCents)}</td>
                            <td>
                              <div>{formatMoney(row.accountWallet?.fundingExposure?.projectedAvailableBalanceCents ?? row.fundingExposure?.projectedAvailableBalanceCents)}</div>
                              {(row.accountWallet?.fundingExposure?.riskState || row.fundingExposure?.riskState) ? (
                                <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>
                                  {titleCaseWords(row.accountWallet?.fundingExposure?.riskState || row.fundingExposure?.riskState)}
                                </div>
                              ) : null}
                            </td>
                            <td>{row.wallet?.state || '—'}</td>
                            <td>{`${Number(row.stores?.active || 0)} / ${Number(row.stores?.total || 0)}`}</td>
                            <td>{row.wallet?.lastTopupAt ? new Date(row.wallet.lastTopupAt).toLocaleString() : '—'}</td>
                            <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                            <td>

                              {(() => {

                                const blockers = deriveQuickBlockers(row).slice(0, 3);

                                const storeInstances = (Array.isArray(row?.blockers?.store) ? row.blockers.store : []).filter((blocker) => blocker?.storeId);

                                const breakdownKey = String(row.id || row.brandId || '');

                                const showBreakdown = expandedStoreBreakdowns[breakdownKey] === true;

                                if (blockers.length === 0) {

                                  return <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}><span className="ops-chip good">None</span></div>;

                                }

                                const actionable = blockers.filter((blocker) => !!blockerActionForCode(blocker.code));

                                const informational = blockers.filter((blocker) => !blockerActionForCode(blocker.code));

                                const renderChip = (blocker, clickable) => {

                                  const detailHref = buildDetailHref(row, blocker);

                                  const tooltip = `${formatBlockerScope(blocker.scope)}: ${formatBlockerBadgeLabel(blocker)}${blocker.causeCode ? ` - ${formatCauseLabel(blocker.causeCode, blocker.causeNote)}` : ''}${clickable ? ' - Open the matching action in detail.' : ''}`;

                                  if (!clickable) {

                                    return (

                                      <span

                                        key={`${blocker.scope}:${blocker.code}`}

                                        className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}

                                        style={{ fontSize: 12 }}

                                        title={tooltip}

                                      >

                                        {formatBlockerBadgeLabel(blocker)}

                                      </span>

                                    );

                                  }

                                  return (

                                    <button

                                      key={`${blocker.scope}:${blocker.code}`}

                                      type="button"

                                      className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}

                                      style={{

                                        fontSize: 12,

                                        border: 'none',

                                        cursor: 'pointer',

                                        textDecoration: 'underline',

                                        textUnderlineOffset: 2,

                                        boxShadow: 'inset 0 -1px 0 rgba(15, 23, 42, 0.18)',

                                      }}

                                      title={tooltip}

                                      onClick={() => navigate(detailHref)}

                                    >

                                      {formatBlockerBadgeLabel(blocker)}

                                    </button>

                                  );

                                };

                                return (

                                  <div style={{ display: 'grid', gap: 6 }}>

                                    {actionable.length ? (

                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>

                                        <span style={{ fontSize: 11, color: '#475569', fontWeight: 700 }}>Action required</span>

                                        {actionable.map((blocker) => renderChip(blocker, true))}

                                      </div>

                                    ) : null}

                                    {informational.length ? (

                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>

                                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>Informational</span>

                                        {informational.map((blocker) => renderChip(blocker, false))}

                                      </div>

                                    ) : null}

                                    {storeInstances.length ? (

                                      <div style={{ display: 'grid', gap: 6 }}>

                                        <button

                                          type="button"

                                          className="ops-btn"

                                          onClick={() => setExpandedStoreBreakdowns((prev) => ({ ...prev, [breakdownKey]: !showBreakdown }))}

                                          aria-expanded={showBreakdown}

                                          style={{ width: 'fit-content', padding: '4px 8px' }}

                                          title="Show the specific stores affected by store-level blockers."

                                        >

                                          {showBreakdown ? 'Hide affected stores' : `Show affected stores (${storeInstances.length})`}

                                        </button>

                                        {showBreakdown ? (

                                          <div style={{ display: 'grid', gap: 6, paddingLeft: 4 }}>

                                            {storeInstances.map((blocker, idx) => {

                                              const href = buildDetailHref(row, blocker);

                                              const action = getDirectBlockerAction(blocker);

                                              return (

                                                <button

                                                  key={`${blocker.storeId || 'store'}:${blocker.code}:${idx}`}

                                                  type="button"

                                                  className="ops-btn"

                                                  onClick={() => navigate(href)}

                                                  title={`${formatBlockerScope(blocker.scope)}: ${formatStoreBlockerEntry(blocker)}${action ? ' - Open the matching action in detail.' : ' - Open detail.'}`}

                                                  style={{

                                                    justifyContent: 'flex-start',

                                                    textAlign: 'left',

                                                    padding: '6px 10px',

                                                    borderColor: 'rgba(15, 23, 42, 0.08)',

                                                    background: '#f8fafc',

                                                  }}

                                                >

                                                  {formatStoreBlockerEntry(blocker)}

                                                </button>

                                              );

                                            })}

                                          </div>

                                        ) : null}

                                      </div>

                                    ) : null}

                                  </div>

                                );

                              })()}

                            </td>
                            <td>
                        <button
                          type="button"
                          className="ops-btn"
                          title="Open business detail."
                          onClick={() => navigate(`/ops/businesses/${row.id || row.brandId}`)}
                        >
                                Open detail
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="ops-nfp-filter-actions">
            <button
              type="button"
              className="ops-btn"
              disabled={page <= 1}
              onClick={() => {
                const p = new URLSearchParams(searchParams);
                p.set('page', String(Math.max(1, page - 1)));
                setSearchParams(p);
              }}
            >
              Previous
            </button>
            <div style={{ padding: '7px 10px' }}>{`Page ${page} of ${totalPages}`}</div>
            <button
              type="button"
              className="ops-btn"
              disabled={page >= totalPages}
              onClick={() => {
                const p = new URLSearchParams(searchParams);
                p.set('page', String(Math.min(totalPages, page + 1)));
                setSearchParams(p);
              }}
            >
              Next
            </button>
          </div>
        </section>
      </div>
    </OpsLayout>
  );
}
